import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import useHandleTrackEvent from '@/hooks/useHandleTrackEvent';
import { RAIL_PX } from '../../../sidebarMetrics';
import { TOPBAR_PX } from '../../../pageMetrics';
import ViewAsBanner from './ViewAsBanner';
import SidebarAccountRow from './SidebarAccountRow';
import SidebarNav from './SidebarNav';

/**
 * The two-level sidebar's first level, Slack's workspace rail: the logo, one
 * icon-and-label tile per nav group, and the account row.
 *
 * The tiles are `SidebarNav`'s own groups in their `rail` variant. Hovering
 * one floats the group's options in the collapsed sidebar's flyout, but only
 * while the second level is collapsed. Clicking one goes to the group's first
 * page; with the second level open it also scrolls the panel to the group,
 * and with it collapsed it leaves it collapsed.
 */
const NavRail = ({ selectedGroup, onSelectGroup, underTopbar = false }) => {
  const theme = useTheme();
  const handleTrackEvent = useHandleTrackEvent();

  return (
    <Box
      data-testid="sidebar-rail"
      component="nav"
      aria-label="Navigation groups"
      sx={{
        width: RAIL_PX,
        flexShrink: 0,
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // The page's tinted background -- the topbar's surface too, so the
        // rail and the topbar read as one frame -- a step off the panel's
        // `sidebar` surface beside it.
        backgroundColor: theme.palette.custom.background,
        position: 'relative',
        // The rail's edge starts below the topbar: along the topbar there is
        // no seam, which is what merges the two.
        '&::after': {
          content: '""',
          position: 'absolute',
          top: underTopbar ? TOPBAR_PX - 1 : 0,
          bottom: 0,
          right: 0,
          width: '1px',
          backgroundColor: theme.palette.custom.divider,
        },
      }}
    >
      {/* The logo sits in the topbar's band, centred on the bar's height. */}
      <Box
        sx={{
          height: underTopbar ? TOPBAR_PX : 'auto',
          paddingY: underTopbar ? 0 : 1.5,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          data-trackid={'logo_clicked'}
          component="a"
          href={'https://dealcopilot.ai/'}
          title={'Deal Copilot'}
          onClick={(e) => handleTrackEvent(e, 'logo_clicked')}
          // Sized to sit inside the topbar's band when it shares it.
          sx={{
            display: 'flex',
            width: underTopbar ? TOPBAR_PX - 15 : 32,
            height: underTopbar ? TOPBAR_PX - 15 : 32,
          }}
        >
          <Box
            component={'img'}
            src={'/dh-icons/logo.svg'}
            height={1}
            width={1}
            sx={{ filter: 'var(--dc-logo-filter)' }}
          />
        </Box>
      </Box>
      <ViewAsBanner compact />
      <Box sx={{ flexGrow: 1, minHeight: 0, width: 1 }}>
        <SidebarNav
          rail
          selectedGroup={selectedGroup}
          onSelectGroup={onSelectGroup}
        />
      </Box>
      <Box sx={{ width: 1, paddingX: 0.5, paddingY: 0.5 }}>
        <SidebarAccountRow collapsed />
      </Box>
    </Box>
  );
};

NavRail.propTypes = {
  selectedGroup: PropTypes.string,
  onSelectGroup: PropTypes.func.isRequired,
  underTopbar: PropTypes.bool,
};

export default NavRail;
